import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, X } from "lucide-react";
import { api, formatApiError, imgSrc } from "../../lib/api";
import { Switch } from "../../components/ui/switch";

const field = "w-full rounded-xl border border-[#EBD08A] bg-white px-4 py-3 outline-none focus:border-[#FF1F8E]";
export const EMPTY_PROMO = { title: "", description: "", deals: [], extras: [], note: "", button_text: "", link_url: "", active: true, starts_on: "", ends_on: "" };

const L = ({ t, hint, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="mb-1 block text-sm font-semibold">{t}</span>
    {children}
    {hint && <span className="mt-1 block text-xs text-black/50">{hint}</span>}
  </label>
);

const DealsEditor = ({ deals, onChange }) => {
  const set = (i, k, v) => onChange(deals.map((d, j) => (j === i ? { ...d, [k]: v } : d)));
  return (
    <div className="space-y-2" data-testid="promo-deals-editor">
      {deals.map((d, i) => (
        <div key={i} className="flex gap-2">
          <input value={d.label} onChange={(e) => set(i, "label", e.target.value)} placeholder="e.g. 1 Christmas tee" className={field} data-testid={`promo-deal-label-${i}`} />
          <input value={d.price} onChange={(e) => set(i, "price", e.target.value)} placeholder="$25" className={`${field} !w-28`} data-testid={`promo-deal-price-${i}`} />
          <button type="button" onClick={() => onChange(deals.filter((_, j) => j !== i))} className="rounded-full p-2 text-black/50 hover:text-red-600" aria-label="Remove price" data-testid={`promo-deal-remove-${i}`}><X className="h-4 w-4" /></button>
        </div>
      ))}
      {deals.length < 8 && <button type="button" onClick={() => onChange([...deals, { label: "", price: "" }])} className="flex items-center gap-1 text-sm font-semibold text-[#FF1F8E]" data-testid="promo-deal-add"><Plus className="h-4 w-4" />Add a price</button>}
    </div>
  );
};

export const PromoForm = ({ initial, onSaved, onCancel }) => {
  const [p, setP] = useState({ ...EMPTY_PROMO, ...initial, extrasText: (initial?.extras || []).join("\n") });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setP({ ...p, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const { extrasText, id, image_url, storage_path, created_at, ...rest } = p;
    const body = { ...rest, extras: extrasText.split("\n").map((x) => x.trim()).filter(Boolean), deals: p.deals.filter((d) => d.label.trim() && d.price.trim()) };
    try {
      let { data } = id ? await api.put(`/admin/promotions/${id}`, body) : await api.post("/admin/promotions", body);
      setP((cur) => ({ ...cur, id: data.id }));
      if (file) {
        const fd = new FormData();
        fd.append("image", file);
        data = (await api.post(`/admin/promotions/${data.id}/image`, fd)).data;
      }
      const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
      const msg = !body.active ? "Promotion saved (turned off)"
        : body.starts_on && body.starts_on > today ? "Promotion saved. It will start showing on its start date"
        : body.ends_on && body.ends_on < today ? "Promotion saved, but its end date has passed so it isn't showing"
        : "Promotion saved and showing on your site";
      toast.success(msg);
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="space-y-5 rounded-3xl bg-white p-6 ring-2 ring-[#FF1F8E] sm:p-8" data-testid="promo-form">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-2xl font-bold">{p.id ? "Edit promotion" : "New promotion"}</h2>
        <label className="flex items-center gap-2 text-sm font-semibold">
          <Switch checked={p.active} onCheckedChange={(v) => setP({ ...p, active: v })} data-testid="promo-active-switch" />{p.active ? "On" : "Off"}
        </label>
      </div>
      <L t="Title *"><input required value={p.title} onChange={set("title")} className={field} placeholder="e.g. Christmas Matching Family Tees" data-testid="promo-title-input" /></L>
      <L t="Short description"><textarea rows={2} value={p.description} onChange={set("description")} className={field} placeholder="What's special about this deal?" data-testid="promo-description-input" /></L>
      <L t="Prices" hint="Optional. Shows as big price boxes."><DealsEditor deals={p.deals} onChange={(deals) => setP({ ...p, deals })} /></L>
      <L t="Extra details" hint="One per line, e.g. Sizes S–XL, 2X +$5"><textarea rows={3} value={p.extrasText} onChange={set("extrasText")} className={field} data-testid="promo-extras-input" /></L>
      <L t="Small print"><input value={p.note} onChange={set("note")} className={field} placeholder="e.g. Order by Dec 10 for Christmas delivery" data-testid="promo-note-input" /></L>
      <div className="grid gap-4 sm:grid-cols-2">
        <L t="Button text" hint="Leave empty for 'Start a Custom Order'"><input value={p.button_text} onChange={set("button_text")} className={field} placeholder="Preorder now" data-testid="promo-button-input" /></L>
        <L t="Button link" hint="Leave empty to open your order form, or paste a Google Form link (https://…)"><input value={p.link_url} onChange={set("link_url")} className={field} placeholder="https://…" data-testid="promo-link-input" /></L>
        <L t="Start showing on" hint="Optional. Prepare it early."><input type="date" value={p.starts_on} onChange={set("starts_on")} className={field} data-testid="promo-start-input" /></L>
        <L t="Stop showing after" hint="Optional. Hides automatically."><input type="date" value={p.ends_on} onChange={set("ends_on")} className={field} data-testid="promo-end-input" /></L>
      </div>
      <L t="Picture (optional)">
        <div className="flex items-center gap-3">
          {(file || imgSrc(p)) && <img src={file ? URL.createObjectURL(file) : imgSrc(p)} alt="" className="h-16 w-16 rounded-xl object-cover" />}
          <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="promo-image-input" />
        </div>
      </L>
      <div className="flex flex-wrap gap-2">
        <button disabled={busy} className="btn-pink !py-3 disabled:opacity-60" data-testid="promo-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save promotion</button>
        <button type="button" onClick={onCancel} className="btn-outline-gold !py-3" data-testid="promo-cancel-btn">Cancel</button>
      </div>
    </form>
  );
};
